import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Card from 'ui/canon/Card';
import AccentSection from 'ui/canon/AccentSection';
import StatCard from 'ui/canon/StatCard';

const ReviewAndPublish = ({ formData, updateFormData, errors, intl, company }) => {
  const hideListPrice =
    (formData.deal_type === 'continuity' && company?.hide_list_price_in_deal_room_wizard_continuity) ||
    (formData.deal_type === 'succession' && company?.hide_list_price_in_deal_room_wizard_succession) ||
    (formData.deal_type === 'sale' && company?.hide_list_price_in_deal_room_wizard_sale);

  const scrollToPublishButton = useCallback((event) => {
    event.preventDefault();
    const publishButton = document.getElementById('deal-wizard-publish-button');
    if (!publishButton) return;

    publishButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    publishButton.focus({ preventScroll: true });
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.common.tbd' });
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const locale = (typeof window !== 'undefined' && window.locale) || 'en';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCompletionStats = () => {
    const fields = [
      formData.deal_type,
      formData.headline,
      formData.sub_headline,
      ...(!hideListPrice ? [formData.list_price !== undefined] : []),
      formData.proximity_max,
      formData.invitation_deadline
    ];
    const completed = fields.filter(Boolean).length;
    const total = fields.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  const stats = getCompletionStats();

  const ProgressRing = ({ percentage, size = 120 }) => {
    const radius = (size - 16) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="var(--succession-border)"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#10b981"
            strokeWidth="8"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-light text-[var(--succession-text-primary)]">{percentage}{intl.locale === 'fr' ? '\u00a0%' : '%'}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--succession-bg-secondary)]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[var(--succession-bg-tertiary)]/30"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 bg-[var(--succession-bg-primary)] border border-[var(--succession-border)] rounded-3xl shadow-succession-md transition-theme duration-theme">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-succession-lg" style={{ backgroundColor: 'var(--succession-primary)' }}>
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-5xl font-thin text-[var(--succession-text-primary)] mb-4 tracking-tight">
              <FormattedMessage id="succession_deals.wizard.steps.review_publish.hero.title" />
            </h1>
            <p className="text-xl font-light text-[var(--succession-text-secondary)] max-w-2xl mx-auto">
              <FormattedMessage id="succession_deals.wizard.steps.review_publish.hero.description" />
            </p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <StatCard 
              value={formData.deal_type?.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.common.na' })} 
              label={intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.stats.deal_type' })} 
              color="indigo"
            />
            <StatCard 
              value={formData.proximity_max || 0} 
              label={intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.stats.max_distance' })} 
              suffix=" mi"
              color="blue"
            />
            <StatCard 
              value={formatDate(formData.invitation_deadline) || intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.common.tbd' })} 
              label={intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.stats.deadline' })}
              color="purple"
            />
            {!hideListPrice && (
              <StatCard 
                value={formatCurrency(formData.list_price) || intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.common.tbd' })} 
                label={intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.stats.price' })}
                color="emerald"
              />
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid lg:grid-cols-3 gap-12">
          
          {/* Left Column - Opportunity Details */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Headline Section */}
            <Card variant="outlined" size="lg" className="rounded-3xl">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v1M7 4V3a1 1 0 011-1h8a1 1 0 011 1v1m-9 4h10m-10 0V7a1 1 0 011-1h8a1 1 0 011 1v1M8 8v10h8V8" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-light text-[var(--succession-text-primary)] mb-2">
                    <FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.title" />
                  </h2>
                  <div className="h-px bg-gradient-to-r from-blue-500 to-transparent w-16"></div>
                </div>
              </div>
              
              <div className="space-y-6">
                {formData.headline && (
                  <div>
                    <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-2">{formData.headline}</h3>
                  </div>
                )}
                
                {formData.sub_headline && (
                  <div className="bg-[var(--succession-bg-secondary)] rounded-2xl p-6">
                    <p className="text-[var(--succession-text-secondary)] leading-relaxed">{formData.sub_headline}</p>
                  </div>
                )}
                
                {(formData.summary_of_business || formData.ideal_candidate || formData.growth_opportunities) && (
                  <div className="grid gap-4">
                    {formData.summary_of_business && (
                      <AccentSection
                        title={<FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.business_summary_label" />}
                        accent="blue"
                      >
                        {formData.summary_of_business}
                      </AccentSection>
                    )}
                    
                    {formData.ideal_candidate && (
                      <AccentSection
                        title={<FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.ideal_candidate_label" />}
                        accent="purple"
                      >
                        {formData.ideal_candidate}
                      </AccentSection>
                    )}
                    
                    {formData.growth_opportunities && (
                      <AccentSection
                        title={<FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.growth_opportunities_label" />}
                        accent="indigo"
                      >
                        {formData.growth_opportunities}
                      </AccentSection>
                    )}

                    {formData.assets_included && (
                      <AccentSection
                        title={<FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.assets_included_label" />}
                        accent="yellow"
                      >
                        {formData.assets_included}
                      </AccentSection>
                    )}

                    {formData.intangible_assets_included && (
                      <AccentSection
                        title={<FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.intangible_assets_included_label" />}
                        accent="green"
                      >
                        {formData.intangible_assets_included}
                      </AccentSection>
                    )}
                    
                    {formData.customer_demographics_comments && (
                      <AccentSection
                        title={<FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.client_demographics_label" />}
                        accent="red"
                      >
                        {formData.customer_demographics_comments}
                      </AccentSection>
                    )}

                    {formData.products_and_services_comments && (
                      <AccentSection
                        title={<FormattedMessage id="succession_deals.wizard.steps.review_publish.opportunity.products_and_services_label" />}
                        accent="pink"
                      >
                        {formData.products_and_services_comments}
                      </AccentSection>
                    )}
                    
                  </div>
                )}
              </div>
            </Card>

            {/* Match Preferences Visual */}
            {(formData.proximity_max || formData.affiliation || formData.practice_similarity || formData.practice_structure || formData.servicing) && (
              <Card variant="outlined" size="lg" className="rounded-3xl overflow-hidden">
                <div className="space-y-6">
                  {/* Distance Preference - Special Treatment */}
                  {formData.proximity_max && (
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-succession-primary/10 to-succession-secondary/10 rounded-2xl opacity-50"></div>
                      <div className="relative text-center p-8 bg-gradient-to-br from-succession-primary/5 to-succession-secondary/5 rounded-2xl border border-succession-primary/20">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg" style={{ backgroundColor: 'var(--succession-primary)' }}>
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div className="text-4xl font-ultralight text-succession-primary mb-2">{formData.proximity_max}</div>
                        <div className="text-sm font-semibold text-succession-text-primary uppercase tracking-wide">
                          <FormattedMessage id="succession_deals.wizard.steps.review_publish.match_preferences.miles_maximum_range" />
                        </div>
                        <div className="text-xs text-succession-primary mt-2">
                          <FormattedMessage id="succession_deals.wizard.steps.review_publish.match_preferences.geographic_search_radius" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Other Preferences Grid */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { key: 'practice_similarity', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.practice_product_mix_label' }), icon: '🎯', desc: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.practice_product_mix_desc' }) },
                      { key: 'practice_structure', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.team_structure_label' }), icon: '👥', desc: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.team_structure_desc' }) },
                      { key: 'servicing', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.client_demographics_preference_label' }), icon: '🏛️', desc: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.client_demographics_preference_desc' }) },
                      { key: 'affiliation', label: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.network_affiliation_label' }), icon: '🏢', desc: intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.network_affiliation_desc' }) }
                    ].filter(item => formData[item.key]).map((item, index) => {
                      const value = formData[item.key];
                      const importance = value === '0' ? 
                        intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.low_priority' }) : 
                        value === '50' ? 
                        intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.medium_priority' }) : 
                        intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.match_preferences.high_priority' });
                      const colors = {
                        '0': { bg: 'from-[var(--succession-bg-secondary)] to-[var(--succession-bg-tertiary)]', border: 'border-[var(--succession-border)]', text: 'text-[var(--succession-text-secondary)]', badge: 'bg-[var(--succession-bg-tertiary)] text-[var(--succession-text-secondary)]' },
                        '50': { bg: 'from-amber-50 to-orange-100', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-800' },
                        '100': { bg: 'from-emerald-50 to-green-100', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-800' }
                      };
                      const colorScheme = colors[value];
                      
                      return (
                        <div key={item.key} className={`relative overflow-hidden bg-gradient-to-br ${colorScheme.bg} rounded-2xl border ${colorScheme.border} p-6 group hover:shadow-md transition-all duration-300`}>
                          <div className="flex items-start justify-between mb-4">
                            <div className="text-2xl">{item.icon}</div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colorScheme.badge}`}>
                              {importance}
                            </span>
                          </div>
                          <h4 className={`text-lg font-medium ${colorScheme.text} mb-2`}>{item.label}</h4>
                          <p className="text-sm text-[var(--succession-text-secondary)]">{item.desc}</p>
                          <div className="absolute bottom-0 right-0 w-12 h-12 bg-[var(--succession-bg-primary)]/20 rounded-tl-2xl"></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            )}

            {/* Screening Questions */}
            {(formData.question_1 || formData.question_2 || formData.question_3 || formData.question_4 || formData.question_5) && (
              <Card variant="outlined" size="lg" className="rounded-3xl overflow-hidden">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-light text-[var(--succession-text-primary)] mb-2">
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.screening_questions.title" />
                    </h2>
                    <div className="h-px bg-gradient-to-r from-orange-500 via-red-500 to-transparent w-24"></div>
                    <p className="text-sm text-[var(--succession-text-secondary)] mt-3">
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.screening_questions.description" />
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map(num => {
                    const question = formData[`question_${num}`];
                    return question && (
                      <div key={num} className="group">
                        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100 hover:shadow-md transition-all duration-300">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-black flex items-center justify-center text-sm font-bold shadow-lg">
                                {num}
                              </div>
                            </div>
                            <div className="flex-1 pt-1">
                              <p className="text-[var(--succession-text-primary)] font-medium leading-relaxed">{question}</p>
                              <div className="mt-3 flex items-center space-x-2">
                                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                                <span className="text-xs text-[var(--succession-text-muted)] font-medium">
                                  <FormattedMessage id="succession_deals.wizard.steps.review_publish.screening_questions.required_response" />
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                                 {/* Question Summary */}
                 <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl border border-orange-100">
                   <div className="flex items-center space-x-3">
                     <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center shadow-lg">
                       <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                         <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                       </svg>
                     </div>
                     <div>
                       <div className="text-sm font-semibold text-orange-900">
                         <FormattedMessage 
                           id="succession_deals.wizard.steps.review_publish.screening_questions.summary.questions_configured"
                           values={{ count: [1, 2, 3, 4, 5].filter(num => formData[`question_${num}`]).length }}
                         />
                       </div>
                       <div className="text-xs text-orange-700">
                         <FormattedMessage id="succession_deals.wizard.steps.review_publish.screening_questions.summary.must_answer_all" />
                       </div>
                     </div>
                   </div>
                 </div>
              </Card>
            )}
          </div>

          {/* Right Column - Progress & Actions */}
          <div className="space-y-8">
            
            {/* Completion Status */}
            <Card variant="outlined" size="lg" className="rounded-3xl text-center">
              <h3 className="text-xl font-light text-[var(--succession-text-primary)] mb-8">
                <FormattedMessage id="succession_deals.wizard.steps.review_publish.completion_status.title" />
              </h3>
              <div className="mb-8">
                <ProgressRing percentage={stats.percentage} />
              </div>
              <div className="text-sm text-[var(--succession-text-secondary)] mb-6">
                <FormattedMessage 
                  id="succession_deals.wizard.steps.review_publish.completion_status.sections_completed"
                  values={{ completed: stats.completed, total: stats.total }}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <div className={`w-3 h-3 rounded-full mr-3 ${formData.deal_type ? 'bg-green-500' : 'bg-[var(--succession-border)]'}`}></div>
                  <span className={formData.deal_type ? 'text-[var(--succession-text-primary)]' : 'text-[var(--succession-text-muted)]'}>
                    <FormattedMessage id="succession_deals.wizard.steps.review_publish.completion_status.deal_type" />
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <div className={`w-3 h-3 rounded-full mr-3 ${formData.headline ? 'bg-green-500' : 'bg-[var(--succession-border)]'}`}></div>
                  <span className={formData.headline ? 'text-[var(--succession-text-primary)]' : 'text-[var(--succession-text-muted)]'}>
                    <FormattedMessage id="succession_deals.wizard.steps.review_publish.completion_status.headline" />
                  </span>
                </div>
                {!hideListPrice && (
                  <div className="flex items-center text-sm">
                    <div className={`w-3 h-3 rounded-full mr-3 ${formData.list_price !== undefined ? 'bg-green-500' : 'bg-[var(--succession-border)]'}`}></div>
                    <span className={formData.list_price !== undefined ? 'text-[var(--succession-text-primary)]' : 'text-[var(--succession-text-muted)]'}>
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.completion_status.pricing" />
                    </span>
                  </div>
                )}
                <div className="flex items-center text-sm">
                  <div className={`w-3 h-3 rounded-full mr-3 ${formData.proximity_max ? 'bg-green-500' : 'bg-[var(--succession-border)]'}`}></div>
                  <span className={formData.proximity_max ? 'text-[var(--succession-text-primary)]' : 'text-[var(--succession-text-muted)]'}>
                    <FormattedMessage id="succession_deals.wizard.steps.review_publish.completion_status.preferences" />
                  </span>
                </div>
              </div>
            </Card>

            {/* Launch Panel */}
            <div className="rounded-3xl p-8 text-white shadow-xl" style={{ backgroundColor: 'var(--succession-primary)' }}>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-light mb-4">
                  <FormattedMessage id="succession_deals.wizard.steps.review_publish.launch_panel.title" />
                </h3>
                <p className="text-white/90 text-sm leading-relaxed mb-8">
                  <FormattedMessage id="succession_deals.wizard.steps.review_publish.launch_panel.description" />
                </p>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-xs font-medium">
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.launch_panel.features.smart_match" />
                    </div>
                  </div>
                  <div>
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-xs font-medium">
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.launch_panel.features.secure" />
                    </div>
                  </div>
                  <div>
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-xs font-medium">
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.launch_panel.features.professional" />
                    </div>
                  </div>
                </div>

                {/* Instructions for Publishing */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-white mb-3">
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.launch_panel.instructions.title" />
                    </h4>
                    <p className="text-white/80 text-sm leading-relaxed mb-4">
                      {stats.percentage >= 80 
                        ? intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.launch_panel.instructions.ready_to_publish' })
                        : intl.formatMessage({ 
                            id: 'succession_deals.wizard.steps.review_publish.launch_panel.instructions.complete_remaining' 
                          }, { percentage: 100 - stats.percentage })
                      }
                    </p>
                    <div className="flex items-center justify-center space-x-2 text-sm">
                      <div className={`w-3 h-3 rounded-full ${stats.percentage >= 80 ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
                      {stats.percentage >= 80 ? (
                        <a
                          href="#deal-wizard-publish-button"
                          onClick={scrollToPublishButton}
                          className="text-white underline underline-offset-2 hover:text-white/80 focus:outline-none focus:ring-2 focus:ring-white/60 rounded-sm"
                        >
                          {intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.launch_panel.instructions.ready_status' })}
                        </a>
                      ) : (
                        <span className="text-white/90">
                          {intl.formatMessage({ id: 'succession_deals.wizard.steps.review_publish.launch_panel.instructions.almost_ready_status' })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-red-900 mb-2">
                      <FormattedMessage id="succession_deals.wizard.steps.review_publish.errors.title" />
                    </h4>
                    <div className="space-y-1">
                      {Object.entries(errors).map(([field, error]) => (
                        <p key={field} className="text-sm text-red-800">{error}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ReviewAndPublish.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  intl: PropTypes.object.isRequired,
  company: PropTypes.object
};

ReviewAndPublish.defaultProps = {
  errors: {},
  company: {}
};

export default injectIntl(ReviewAndPublish); 